import {
  ExtError,
  evalExtErrorFaultsText,
} from '@navify-platform/error';

export type RequestInit = {
  headers?: Record<string, string>,
  body?: any,
  retryTimes?: number,
  retryAfter?: number,
  retryIf?: (any) => Promise<boolean>,
};

export async function request(
  method: string,
  url: string,
  init?: RequestInit,
): Promise<any> {
  return window.fetch(url, {
    method,
    headers: new Headers({
      'Content-Type': 'application/json', // enforce CORS preflight
      ...(init && init.headers || {}),
    }),
    body: (init && init.body || null),
    mode: 'cors',
    credentials: 'include',
  })
    .then(throwOnResponseError)
    .then(getResponseJson)
    .catch(throwOnCaught)
    .catch(checkRetry(method, url, init));
}

export async function getRequest(
  url: string,
  init?: RequestInit,
): Promise<any>  {
  return request('GET', url, init);
}

export async function postRequest(
  url: string,
  init?: RequestInit,
): Promise<any> {
  return request('POST', url, init);
}

export async function putRequest(
  url: string,
  init?: RequestInit,
): Promise<any> {
  return request('PUT', url, init);
}

/**
 * @hidden
 */
 export async function deleteRequest(
  url: string,
  init?: RequestInit,
): Promise<any> {
  return request('DELETE', url, init);
}

/**
 * @hidden
 */
export const HTTP_ERRORS_STATUS_FAULT_CHECKS = {
  httpClientError: (status: number) => (status >= 400 && status < 500),
  httpBadRequest: 400,
  httpUnauthorized: 401,
  httpForbidden: 403,
  httpNotFound: 404,
  httpRequestTimeout: 408,
  httpServerError: (status: number) => (status >= 500 && status < 600),
  httpInternalServerError: 500,
};

/**
 * @hidden
 */
export function evalResponseErrorFaults(
  response: Response,
): Record<string, any> {
  const responseFaults = Object.keys(HTTP_ERRORS_STATUS_FAULT_CHECKS).reduce(
    (faults: Record<string, any>, faultKey: string) => {
      const statusFailureCheck = HTTP_ERRORS_STATUS_FAULT_CHECKS[faultKey];
      const statusFailure = (typeof statusFailureCheck === 'function') ?
        statusFailureCheck(response.status) :
        (response.status === statusFailureCheck);
      if (statusFailure) {
        faults[faultKey] = statusFailure;
      }
      return faults;
    },
    {},
  );
  return responseFaults;
}

/**
 * @hidden
 * Authentication HTTP error object.
 */
export class HttpError extends ExtError {
  readonly name: string = 'HttpError';
  readonly message: string = null;

  /**
   * Constructor.
   * @param customFaults Custom faults object (optional)
   * @param response HTTP response (optional)
   * @param errorThrown Originally thrown error object (optional)
   */
  constructor(
    customFaults?: Record<string, boolean>,
    readonly response?: Response,
    errorThrown?: Error,
  ) {
    super({ httpError: true, ...(customFaults || {}) }, errorThrown);

    if (this.originalError) {
      this.faults.connectionError = true;
    }

    if (response) {
      const responseFaults = evalResponseErrorFaults(response);
      Object.assign(this.faults, responseFaults);
    }

    const faultsText = evalExtErrorFaultsText(this.faults);
    const statusText = this.response ? this.response.statusText : '-';
    this.message = `${faultsText}; ${statusText}`;
  }
}

/**
 * @hidden
 */
export async function throwOnResponseError(
  response: Response,
): Promise<Response> {
  if (response.ok) {
    return response;
  }

  const httpError = new HttpError(null, response);
  throw httpError;
}

/**
 * @hidden
 */
export async function throwOnCaught(
  error: any,
): Promise<void> {
  let response = null;
  try {
    response = await getResponseJson(error.response);
  } catch (err) {
    // noop
  }

  const httpError = new HttpError({ response }, null, error);
  throw httpError;
}

/**
 * @hidden
 */
export async function getResponseJson(
  response: Response,
): Promise<any> {
  return response.json();
}

/**
 * @hidden
 */
export function checkRetry(
  method: string,
  url: string,
  init: RequestInit = {},
): (any) => Promise<any> {
  return async (
    error: any,
  ): Promise<any> => {
    const retryTimes = init.retryTimes || 0;
    const retryAfter = init.retryAfter || 0;
    const retryIf = init.retryIf || (() => true);

    if (retryTimes && await retryIf(error)) {
      return new Promise((resolve, reject) => setTimeout(
        () => request(method, url, { ...init, retryTimes: retryTimes - 1 }).then(resolve).catch(reject),
        retryAfter,
      ));
    } else {
      throw error;
    }
  };
}
